import PageLoadErrorAlertContent from '@/components/error/PageLoadErrorAlertContent';
import { ErxStatusChip } from '@/components/prescriptions/ErxStatusChip';
import { PRESCRIPTION_ROUTE } from '@/routes/route-paths';

import { Logger } from '@/utils/logger';
import type { apiCustomerPortalContract } from '@montugroup/circuit-api-contracts';
import { getTsCustomerPortalClient } from '@montugroup/circuit-shared/api/customer-portal.client';
import { CircularProgressWrapper } from '@montugroup/circuit-shared/components/common/CircularProgressWrapper';
import type { ArrayElement } from '@montugroup/circuit-shared/utils/typscript-helpers';
import { Alert, AlertTitle, Button, Link, Stack } from '@montugroup/design-system';
import type { ClientInferResponseBody } from '@ts-rest/core';
import type { LinkProps as RouterLinkProps } from 'react-router-dom';
import { generatePath, Link as RouterLink } from 'react-router-dom';

const logger = new Logger('PrescriptionsList');
const client = getTsCustomerPortalClient();

const PrescriptionsListRow = ({
  prescriptionProducts,
  id,
}: ArrayElement<
  ClientInferResponseBody<typeof apiCustomerPortalContract.prescription.listPrescriptions, 200>['data']
>) => {
  // HACK: We are assuming prescriptions can only have 1 product for this UI
  const prescriptionProduct = prescriptionProducts[0];
  let prescriptionTitle = prescriptionProduct?.name;
  const erxStatus = prescriptionProduct?.erxStatus;

  if (!prescriptionTitle) {
    logger.critical('no prescriptionTitle, using fallback title', {
      id,
      prescriptionProducts,
    });

    // This fallback title should never be hit, but this will just allow the UI to still be usable if it was
    prescriptionTitle = `Prescription ${id}`;
  }

  if (prescriptionProducts.length !== 1) {
    logger.critical('prescriptionProducts.length !== 1', { id, prescriptionProducts });
  }

  const routerStateProps = {
    state: {
      token: prescriptionTitle,
    },
  } satisfies Pick<RouterLinkProps, 'state'>;

  return (
    <Stack
      key={id}
      direction="row"
      justifyContent="space-between"
      alignItems="center"
      sx={{
        padding: 4,
        border: '1px solid',
        borderRadius: '8px',
        borderColor: (theme) => theme.palette.action.disabled,
      }}
    >
      <Link
        component={RouterLink}
        variant="subtitle2"
        fontWeight={600}
        to={generatePath(PRESCRIPTION_ROUTE, { id: `${id}` })}
        {...routerStateProps}
      >
        {prescriptionTitle}
      </Link>
      {erxStatus && <ErxStatusChip status={erxStatus} />}
    </Stack>
  );
};

export function PrescriptionsList() {
  const { data, isLoading, isError, hasNextPage, fetchNextPage, isFetchingNextPage, refetch } =
    client.prescription.listPrescriptions.useInfiniteQuery({
      queryKey: client.prescription.listPrescriptions.keys,
      queryData: ({ pageParam }: { pageParam: string | undefined }) => ({
        query: { page: { after: pageParam, size: 10 } },
      }),
      initialPageParam: undefined,
      getNextPageParam: (lastPage) => {
        return lastPage.body.meta?.page?.cursor.next ?? undefined;
      },
    });

  const prescriptions = data?.pages.flatMap((page) => page.body.data) ?? [];

  const handleClickRetry = () => refetch();

  if (isLoading) {
    return <CircularProgressWrapper />;
  }

  if (isError && !data) {
    return (
      <Alert severity="error">
        <AlertTitle>Page load error</AlertTitle>
        <PageLoadErrorAlertContent onClickRetry={handleClickRetry} />
      </Alert>
    );
  }

  return (
    <Stack gap={3}>
      {prescriptions.map((prescription) => (
        <PrescriptionsListRow {...prescription} key={prescription.id} />
      ))}
      {isError && data && (
        <Alert severity="error">
          <AlertTitle>Failed to load more prescriptions</AlertTitle>
          <PageLoadErrorAlertContent onClickRetry={() => fetchNextPage()} />
        </Alert>
      )}
      {!isError && hasNextPage && (
        <Button variant="outlined" onClick={() => fetchNextPage()} disabled={isFetchingNextPage}>
          {isFetchingNextPage ? 'Loading...' : 'Show more'}
        </Button>
      )}
    </Stack>
  );
}
